/**
 * Thrivia Embed SDK: telemetry beacons.
 *
 * Purpose: the only outbound write the loader makes. Posts `{ sid, event, surface, platform, path }` to
 * `{apiBase}embed/track`, which counts it against the embed session the batched read minted.
 *
 * The governing rule is that telemetry must never be visible to a visitor or to the organizer whose
 * page we are standing on. Every failure is swallowed, nothing is retried, no response is ever
 * read, and no promise is ever left to reject unhandled. A blocked beacon costs us a number in a
 * dashboard; a thrown beacon costs an organizer their homepage.
 *
 * TRANSPORT (ruling R32, and it was reproduced in a real browser rather than reasoned about).
 *
 * The obvious implementation, `sendBeacon` with an `application/json` Blob, delivers ZERO beacons
 * cross-origin. Three facts compose into that:
 *
 * 1. The Beacon spec fixes the request's credentials mode to `include`.
 * 2. An `application/json` Blob is not a safelisted content type, so the request is not simple and
 *    the browser preflights it.
 * 3. The embed router answers `Access-Control-Allow-Origin: *` with no `Allow-Credentials`, which
 *    fails the CORS check for a CREDENTIALED request, so the POST is never sent at all.
 *
 * And `sendBeacon` returns `true` regardless, because it reports QUEUING and not delivery, so a
 * `return` on that value silently skips every fallback. Every impression, click and error from
 * every organizer site would be discarded forever with nothing anywhere to say so.
 *
 * The shape that works:
 *
 * - **Primary: `fetch(..., { keepalive: true, credentials: "omit" })`.** Omitting credentials is
 *   what makes the wildcard `Access-Control-Allow-Origin` acceptable, so the preflight its
 *   `application/json` content type triggers is answered and the POST goes out. Proven 202 through
 *   this exact CORS policy.
 * - **Fallback, only where `keepalive` is unsupported (Firefox before 133): `sendBeacon` with a
 *   `text/plain` Blob.** text/plain IS safelisted, so the request is simple, needs no preflight and
 *   survives the credentialed-request rule. It requires the backend half of R32: the embed router's
 *   parser reads `["application/json", "text/plain"]`, without which the body parses to `{}` and
 *   every fallback beacon 401s, silently.
 * - Last resort, a plain non-keepalive fetch. It cannot survive a page unload, but a beacon that
 *   usually arrives beats one that never does.
 *
 * One detail that survives from the original reasoning: the backend's app-level error handler
 * answers with a PLAINTEXT non-JSON 500, so no response is ever read and no status is ever branched
 * on. The beacon is fire and forget in both transports.
 *
 * Two independent suppressions, and both are enforced here rather than at the call sites, so no
 * future caller can forget one:
 *
 * - `sid === null`: the API declined to mint an attribution session for this origin. Render fully,
 *   send nothing. This is a normal state, not an error.
 * - preview mode (`data-thrivia-preview` on the script tag or on a mount): render fully, send
 *   nothing. It exists for Plan 3's dashboard preview, where a creator inspecting their own snippet
 *   must not manufacture impressions and clicks against their own analytics. It deliberately sits
 *   ON TOP of the sid rule rather than instead of it, so a preview that does receive a usable sid
 *   still sends zero beacons.
 *
 * Event names must come from the allowlist Plan 1 shipped in `EmbedTrackingService`
 * (`ALLOWED_TRACK_EVENTS`); anything else is answered with a 400 and never stored. Task 4 owns the
 * first three, Tasks 6 and 7 add `checkout_open`, `login_start`, `login_complete` and
 * `booking_complete`.
 *
 * No runtime dependencies, ES2019 output target.
 */

/** The subset of the backend allowlist this build emits. Widened as later tasks land. */
export type TrackEventName =
  | "embed_impression"
  | "embed_click"
  | "embed_error"
  /** Fired only when a checkout surface actually reached the visitor. See `overlay.ts`. */
  | "checkout_open";

export interface TrackerOptions {
  /** Always ends with exactly one slash; the path is appended without a leading one. */
  apiBase: string;
  platform: string;
  /** Script-level `data-thrivia-preview`: suppresses every beacon on the page. */
  preview: boolean;
  debug: boolean;
}

/**
 * The fallback Blob's type. Safelisted, therefore a simple request, therefore no preflight and no
 * credentialed-request CORS failure. The backend's embed router parses it (R32 backend half).
 */
const BEACON_CONTENT_TYPE = "text/plain";

/**
 * Whether this host's `fetch` honours `keepalive`. Probed once; the answer cannot change.
 *
 * The getter trick is the only reliable detection: an implementation that does not know the option
 * simply never reads it off the init object. Firefox shipped `fetch` in 2015 and `keepalive` only in
 * 133, so "fetch exists" is not the same question.
 */
let keepaliveProbed = false;
let keepaliveWorks = false;

function supportsKeepalive(): boolean {
  if (keepaliveProbed) return keepaliveWorks;
  keepaliveProbed = true;
  try {
    if (typeof fetch !== "function" || typeof Request !== "function") return false;
    let read = false;
    new Request("/", {
      get keepalive(): boolean {
        read = true;
        return true;
      },
    } as RequestInit);
    keepaliveWorks = read;
  } catch {
    keepaliveWorks = false;
  }
  return keepaliveWorks;
}

/** Posts via fetch. Returns false only when the call could not be made at all. */
function postWithFetch(url: string, body: string, keepalive: boolean): boolean {
  try {
    if (typeof fetch !== "function") return false;
    const pending = fetch(url, {
      method: "POST",
      body: body,
      // Survives the page unload that a click navigating away causes.
      keepalive: keepalive,
      // The load-bearing half: an uncredentialed request is allowed to accept a wildcard
      // Access-Control-Allow-Origin, which is exactly what the embed router answers with.
      credentials: "omit",
      mode: "cors",
      headers: { "Content-Type": "application/json" },
    });
    // The response is never read. The backend answers /track failures with a plaintext body, so
    // calling .json() on it would throw inside our own promise chain for no benefit at all, and an
    // unhandled rejection surfaces in the visitor's console and the organizer's error reporting.
    if (pending && typeof pending.catch === "function") pending.catch(() => undefined);
    return true;
  } catch {
    return false;
  }
}

/**
 * Sends one beacon and swallows absolutely everything.
 *
 * Order is the ruling: keepalive fetch, then a text/plain sendBeacon, then a plain fetch. Every
 * step is wrapped, including the promise.
 */
function send(url: string, body: string): void {
  if (supportsKeepalive() && postWithFetch(url, body, true)) return;

  try {
    const beacon = navigator.sendBeacon;
    if (typeof beacon === "function") {
      // Returns false when the agent refuses to QUEUE it. It never reports delivery, so a true here
      // is not evidence the beacon arrived; that is what the browser-level check in Task 8 is for.
      if (beacon.call(navigator, url, new Blob([body], { type: BEACON_CONTENT_TYPE }))) return;
    }
  } catch {
    /* fall through */
  }

  postWithFetch(url, body, false);
}

export interface Tracker {
  /**
   * Records one event. Returns true only when a beacon actually left, which is what the tests
   * assert on and what `debug` reports; callers never branch on it.
   */
  track(
    event: TrackEventName,
    sid: string | null,
    surface: string,
    mountPreview: boolean
  ): boolean;
}

export function createTracker(options: TrackerOptions): Tracker {
  const url = options.apiBase + "embed/track";

  return {
    track(event, sid, surface, mountPreview) {
      // Preview suppresses even when the sid is perfectly usable: it is an OR, not a fallback.
      // One debug line for both suppressions (preview, no sid): the two separate messages cost
      // the gzipped bytes the page-path field below needed under the loader's 10 KiB cap.
      if (options.preview || mountPreview || !sid) {
        if (options.debug) console.log("[thrivia] skip:", event);
        return false;
      }

      send(
        url,
        JSON.stringify({
          sid: sid,
          event: event,
          // Forward-compatible payload, and NOTHING reads it today. The controller destructures
          // `{ sid, event }` only and EmbedTrackingService writes a fixed `$set`, so using these
          // needs a controller change AND a schema change, not just a query.
          //
          // Why send them at all: the sid is shared by every visitor inside the endpoint's 60 second
          // cache window, so the surface and platform baked into it belong to whichever visitor
          // happened to miss the cache. These two are this visitor's own, which is strictly more
          // accurate, and having them already on the wire makes that later change backend-only
          // rather than a new bundle on every organizer's website.
          surface: surface,
          platform: options.platform,
          // The page the widget renders on, pathname ONLY: no query string or hash, which is where
          // tokens and personal data live. The backend validates and bounds it and stores it on
          // impressions, so the weekly backlink check reads this exact page, not the home page.
          path: location.pathname,
        })
      );
      return true;
    },
  };
}
